import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { GitCommitHorizontal, ShieldCheck, ShieldAlert, Radar, FileCode2, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function CheckDetailsDialog({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!id) return;
    setD(null);
    client.get(`/compliance/checks/${id}`).then((r) => setD(r.data));
  }, [id]);
  const byRes = {};
  (d?.violations || []).forEach((v) => { (byRes[v.address] = byRes[v.address] || { file: v.file, items: [] }).items.push(v); });
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="check-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Compliance check details</DialogTitle>
          <DialogDescription>
            {d ? `${d.repo || "ad-hoc"}${d.ref ? ` @ ${d.ref}` : ""} · ${d.actor} · ${new Date(d.at).toLocaleString()}` : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        {d && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className={cn("rounded-full px-3 py-1 font-display font-semibold", d.compliant ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700")} data-testid="check-score">{d.score}% · {d.compliant ? "compliant" : "non-compliant"}</span>
              <span className="rounded-full bg-slate-100 px-3 py-1 font-mono-tight text-xs text-slate-600">{d.summary?.resources} resources</span>
              <span className="rounded-full bg-slate-100 px-3 py-1 font-mono-tight text-xs text-slate-600">{d.summary?.violations} violations</span>
              <span className="rounded-full bg-slate-100 px-3 py-1 font-mono-tight text-xs text-slate-600">{d.summary?.warnings} warnings</span>
              {d.commit && <span className="rounded-full bg-slate-100 px-3 py-1 font-mono-tight text-xs text-slate-600">commit {d.commit.slice(0, 8)}</span>}
            </div>
            <div className="max-h-[420px] space-y-3 overflow-auto" data-testid="check-violations">
              {Object.keys(byRes).length === 0 && <p className="text-sm text-emerald-700" data-testid="check-no-violations">No violation — every resource satisfies the governance.</p>}
              {Object.entries(byRes).map(([addr, g]) => (
                <div key={addr} className="rounded-md border border-rose-100 bg-rose-50/40 p-3" data-testid={`violation-${addr}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono-tight text-xs font-semibold text-slate-900">{addr}</span>
                    <span className="inline-flex items-center gap-1 font-mono-tight text-[10px] text-slate-500"><FileCode2 className="h-3 w-3" /> {g.file}</span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {g.items.map((v) => (
                      <li key={`${v.check}-${v.detail}`} className="flex gap-2 text-xs text-slate-700">
                        <span className="shrink-0 rounded bg-rose-100 px-1.5 font-mono-tight text-[10px] uppercase tracking-widest text-rose-700">{v.check}</span>
                        <span>{v.detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {(d.warnings || []).length > 0 && (
                <div className="rounded-md border border-amber-100 bg-amber-50/40 p-3">
                  <p className="font-mono-tight text-[10px] uppercase tracking-widest text-amber-700">warnings</p>
                  <ul className="mt-1 space-y-0.5 text-xs text-slate-700">{d.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
                </div>
              )}
              {(d.resources || []).length > 0 && (
                <details className="rounded-md border border-slate-200 p-3">
                  <summary className="cursor-pointer font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">all resources ({d.resources.length})</summary>
                  <ul className="mt-2 space-y-0.5 font-mono-tight text-xs">
                    {d.resources.map((r) => <li key={r.address} className="flex justify-between gap-2"><span className="truncate">{r.address}{r.name ? ` (${r.name})` : ""}</span><span className={r.status === "pass" ? "text-emerald-600" : r.status === "warn" ? "text-amber-600" : "text-rose-600"}>{r.status}</span></li>)}
                  </ul>
                </details>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CiChecksPanel() {
  const [checks, setChecks] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    client.get("/compliance/checks?limit=8").then((r) => setChecks(r.data)).catch(() => setChecks([]));
  }, []);

  return (
    <Card className="col-span-full rounded-md border border-slate-200 bg-white p-6 shadow-none" data-testid="ci-checks-panel">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <p className="font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">// ci gate</p>
          <h2 className="mt-1 font-display text-lg font-semibold">Recent compliance checks</h2>
        </div>
        <Radar className="h-4 w-4 text-slate-400" />
      </div>
      {checks === null ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : checks.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="ci-checks-empty">No CI check received yet. Generated pipelines call <code className="font-mono-tight">POST /api/compliance/check</code> before every plan.</p>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="ci-checks-list">
          {checks.map((c) => (
            <button type="button" key={c.id} onClick={() => setOpenId(c.id)} className="flex w-full items-center justify-between gap-4 py-2.5 text-left transition-colors hover:bg-slate-50" data-testid={`ci-check-${c.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-full", c.compliant ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700")}>
                  {c.compliant ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-900">{c.repo || "ad-hoc check"}{c.ref ? <span className="ml-2 font-mono-tight text-[11px] text-slate-500">@ {c.ref}</span> : null}</div>
                  <div className="font-mono-tight text-[11px] text-slate-500">
                    {c.actor} · {c.summary?.resources ?? 0} resources · {c.summary?.violations ?? 0} violations
                    {c.commit && <span className="ml-2 inline-flex items-center gap-1"><GitCommitHorizontal className="h-3 w-3" />{c.commit.slice(0, 8)}</span>}
                  </div>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className={cn("font-display text-base font-semibold", c.compliant ? "text-emerald-700" : "text-rose-700")}>{c.score}%</div>
                <div className="font-mono-tight text-[10px] text-slate-400">{new Date(c.at).toLocaleString()}</div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
            </button>
          ))}
        </div>
      )}
      <CheckDetailsDialog id={openId} onClose={() => setOpenId(null)} />
    </Card>
  );
}
